import { z } from 'zod';
/** The biggest file we accept. Anything larger is refused before it is read. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** How a file is shown in the editor. */
export const fileKindSchema = z.enum(['image', 'video', 'audio', 'pdf', 'file']);
/**
 * The types we accept. An allowlist, not a blocklist: an unknown type is
 * refused instead of stored and served later.
 *
 * SVG is deliberately absent. It can carry script, and we serve files back
 * from our own origin; a raster image or a document does not.
 */
export const ALLOWED_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'video/mp4',
    'video/webm',
    'audio/mpeg',
    'audio/mp4',
    'audio/ogg',
    'audio/wav',
    'audio/flac',
    'application/pdf',
    'text/plain',
    'text/markdown',
    'text/csv',
    'application/json',
    'application/zip',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
const allowed = new Set(ALLOWED_MIME_TYPES);
export function isAllowedMimeType(value) {
    return allowed.has(value);
}
/** The kind of block a file becomes in the editor. */
export function fileKindOf(contentType) {
    if (contentType.startsWith('image/'))
        return 'image';
    if (contentType.startsWith('video/'))
        return 'video';
    if (contentType.startsWith('audio/'))
        return 'audio';
    if (contentType === 'application/pdf')
        return 'pdf';
    return 'file';
}
/** The name a file gets in the editor, without any path. */
export const uploadFilenameSchema = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .transform((name) => name.split(/[\\/]/).pop() ?? name)
    .refine((name) => name.length > 0 && !name.startsWith('.'), {
    message: 'filename_not_allowed',
});
/** The bookkeeping of one file, as the client sees it. */
export const fileViewSchema = z.object({
    id: z.string(),
    pageId: z.string(),
    filename: z.string(),
    contentType: z.string(),
    size: z.number(),
    kind: fileKindSchema,
    createdAt: z.string(),
});
/**
 * The first bytes of a file, so a wrong content type is caught before the
 * bytes are stored. Returns false when the bytes do not match the type.
 */
export function matchesContentType(bytes, contentType) {
    const startsWith = (...signature) => signature.every((byte, index) => bytes[index] === byte);
    switch (contentType) {
        case 'image/png':
            return startsWith(0x89, 0x50, 0x4e, 0x47);
        case 'image/jpeg':
            return startsWith(0xff, 0xd8, 0xff);
        case 'image/gif':
            return startsWith(0x47, 0x49, 0x46);
        case 'image/webp':
            return (bytes.length > 12 &&
                startsWith(0x52, 0x49, 0x46, 0x46) &&
                bytes[8] === 0x57 &&
                bytes[9] === 0x45 &&
                bytes[10] === 0x42 &&
                bytes[11] === 0x50);
        case 'application/pdf':
            return startsWith(0x25, 0x50, 0x44, 0x46);
        case 'video/mp4':
        case 'audio/mp4':
            return bytes.length > 12 && String.fromCharCode(...bytes.slice(4, 8)) === 'ftyp';
        case 'video/webm':
            return startsWith(0x1a, 0x45, 0xdf, 0xa3);
        case 'audio/ogg':
            return startsWith(0x4f, 0x67, 0x67, 0x53);
        case 'audio/wav':
            return (String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' &&
                String.fromCharCode(...bytes.slice(8, 12)) === 'WAVE');
        case 'audio/mpeg':
            return startsWith(0x49, 0x44, 0x33) || bytes[0] === 0xff;
        case 'application/zip':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return startsWith(0x50, 0x4b, 0x03, 0x04);
        case 'application/json':
        case 'text/plain':
        case 'text/markdown':
        case 'text/csv':
            // Text has no signature; it must at least be valid UTF-8 without controls.
            return !hasControlBytes(bytes);
        default:
            return false;
    }
}
/** True when the bytes contain a control character that text may not have. */
function hasControlBytes(bytes) {
    for (const byte of bytes) {
        if (byte === 0)
            return true;
        // Tab, newline and carriage return are fine.
        if (byte < 0x20 && byte !== 0x09 && byte !== 0x0a && byte !== 0x0d)
            return true;
    }
    return false;
}
//# sourceMappingURL=files.js.map